import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Printer, ArrowLeft, Scissors } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { formatApiErrorDetail } from '@/auth/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ROLE_LABEL = { admin: 'admin', manager: '管理者', user: 'ユーザー' };

// Four short black L-shaped corner ticks = the cut marks for each slip.
function CornerTicks() {
  return (
    <>
      <span className="absolute top-0 left-0 w-2.5 h-2.5 border-t-2 border-l-2 border-zinc-900" />
      <span className="absolute top-0 right-0 w-2.5 h-2.5 border-t-2 border-r-2 border-zinc-900" />
      <span className="absolute bottom-0 left-0 w-2.5 h-2.5 border-b-2 border-l-2 border-zinc-900" />
      <span className="absolute bottom-0 right-0 w-2.5 h-2.5 border-b-2 border-r-2 border-zinc-900" />
    </>
  );
}

function CredentialSlip({ cred, loginUrl }) {
  return (
    <div
      className="relative border border-dashed border-zinc-400 bg-white px-4 py-3 flex flex-col justify-between"
      style={{ minHeight: '50mm' }}
      data-testid={`cred-slip-${cred.id}`}
    >
      <CornerTicks />
      <div>
        <div className="flex items-center justify-between mb-1.5">
          <span className="text-[10px] font-bold tracking-wider text-teal-700 uppercase">Sensbey ログイン情報</span>
          <span className="text-[9px] text-zinc-500 border border-zinc-300 rounded px-1.5 py-0.5">{ROLE_LABEL[cred.role] || cred.role}</span>
        </div>
        <p className="text-[15px] font-bold text-zinc-900 leading-tight truncate">{cred.name}</p>
        <dl className="mt-2 space-y-1 text-[11px]">
          <div className="flex gap-2">
            <dt className="text-zinc-500 w-14 shrink-0">メール</dt>
            <dd className="font-mono text-zinc-900 break-all">{cred.email}</dd>
          </div>
          <div className="flex gap-2 items-center">
            <dt className="text-zinc-500 w-14 shrink-0">初期PW</dt>
            <dd className="font-mono font-bold text-zinc-900 tracking-wide bg-zinc-100 border border-zinc-300 rounded px-1.5 py-0.5">{cred.password}</dd>
          </div>
          {loginUrl && (
            <div className="flex gap-2">
              <dt className="text-zinc-500 w-14 shrink-0">URL</dt>
              <dd className="font-mono text-[10px] text-zinc-700 break-all">{loginUrl}</dd>
            </div>
          )}
        </dl>
      </div>
      <p className="mt-2 text-[9px] text-red-600 leading-tight border-t border-zinc-200 pt-1.5">
        ※ セキュリティのため、初回ログイン後に必ずパスワードを変更してください。
      </p>
    </div>
  );
}

export default function CredentialsPrint() {
  const navigate = useNavigate();
  const [creds, setCreds] = useState(null);
  const loginUrl = typeof window !== 'undefined' ? `${window.location.origin}/login` : '';

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/users/initial-credentials`);
        setCreds(data.credentials || []);
      } catch (e) {
        toast.error(formatApiErrorDetail(e.response?.data?.detail) || '認証情報の取得に失敗しました');
        setCreds([]);
      }
    })();
  }, []);

  const canPrint = Array.isArray(creds) && creds.length > 0;

  return (
    <div className="cred-print-root min-h-screen bg-zinc-100">
      {/* Root-named page kills Chromium's trailing blank page (see PrintPage) */}
      <style>{'@media print { html, body, #root { page: credentials-page; } }'}</style>
      {/* Toolbar (hidden on print) */}
      <div className="no-print sticky top-0 z-10 bg-white border-b border-zinc-200 px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button variant="ghost" onClick={() => navigate('/settings/users')} data-testid="cred-back" className="h-9">
            <ArrowLeft size={16} className="mr-1.5" /> ユーザー管理に戻る
          </Button>
          <div className="hidden sm:block">
            <p className="text-sm font-semibold text-zinc-900 flex items-center gap-1.5">
              <Scissors size={16} weight="duotone" className="text-teal-700" /> 認証情報シート (A4・切り取り線付き)
            </p>
            <p className="text-[11px] text-zinc-500">未ログイン（初期パスワード）のアカウントのみ表示します。</p>
          </div>
        </div>
        <Button onClick={() => window.print()} disabled={!canPrint} data-testid="cred-print" className="bg-teal-700 hover:bg-teal-800 h-9 disabled:opacity-50">
          <Printer size={16} className="mr-1.5" /> 印刷
        </Button>
      </div>

      {/* Sheet */}
      <div className="cred-print-body flex justify-center py-6 px-4">
        {creds === null ? (
          <div className="py-24 text-center">
            <div className="w-8 h-8 mx-auto rounded-full border-4 border-zinc-200 border-t-[#0052FF] animate-spin" />
            <p className="mt-3 text-sm text-zinc-500">読み込み中…</p>
          </div>
        ) : creds.length === 0 ? (
          <div className="no-print bg-white rounded-xl border border-zinc-200 px-6 py-16 text-center max-w-md">
            <Scissors size={36} weight="duotone" className="text-zinc-300 mx-auto" />
            <p className="mt-3 text-sm font-semibold text-zinc-700">印刷できる認証情報がありません</p>
            <p className="mt-1 text-xs text-zinc-500">
              「一括登録」で新しいアカウントを作成すると、ここに初期パスワードが表示されます。
              既にログイン済みのユーザーは表示されません。
            </p>
            <Button onClick={() => navigate('/settings/users')} variant="outline" className="mt-4">ユーザー管理に戻る</Button>
          </div>
        ) : (
          <div
            className="print-credentials bg-white shadow-lg"
            style={{ width: '190mm', padding: '4mm' }}
            data-testid="cred-sheet"
          >
            <div className="grid grid-cols-2 gap-3">
              {creds.map((c) => (
                <div key={c.id} style={{ breakInside: 'avoid' }}>
                  <CredentialSlip cred={c} loginUrl={loginUrl} />
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
